import { useEffect, useMemo, useRef } from "react";
import {
  ChatCircleText,
  ClockCountdown,
  FileText,
  CheckCircle,
  Sparkle,
  X,
  ArrowSquareOut,
} from "@phosphor-icons/react";

const iconFor = (value) => {
  const key = String(value || "").toLowerCase();
  if (key.includes("create") || key.includes("capture") || key.includes("feeder")) return Sparkle;
  if (key.includes("text") || key.includes("sms") || key.includes("chat")) return ChatCircleText;
  if (key.includes("email") || key.includes("mail") || key.includes("document")) return FileText;
  if (key.includes("reply") || key.includes("confirm") || key.includes("handled")) return CheckCircle;
  if (key.includes("wait") || key.includes("gate") || key.includes("snooze") || key.includes("clock")) return ClockCountdown;
  return Sparkle;
};

const formatTimestamp = (value) => {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function ProofTrailPopover({ open, obligation, events = [], onClose, onOpenEvidence }) {
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  const orderedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aSeq = a?.seq;
      const bSeq = b?.seq;
      if (aSeq !== null && aSeq !== undefined && bSeq !== null && bSeq !== undefined && aSeq !== bSeq) {
        return aSeq - bSeq;
      }
      const aTime = new Date(a?.timestamp || 0).getTime();
      const bTime = new Date(b?.timestamp || 0).getTime();
      return aTime - bTime;
    });
  }, [events]);

  if (!open || !obligation) return null;

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close proof trail"
        className="absolute inset-0 bg-[rgba(17,32,58,0.18)]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`How I know for ${obligation.title}`}
        className="absolute left-1/2 top-20 z-50 w-[min(92vw,30.625rem)] -translate-x-1/2 overflow-hidden rounded-[1rem] border border-[hsl(var(--input))] bg-card shadow-[0_18px_50px_rgba(17,32,58,0.22)]"
      >
        <div className="flex items-start gap-3 border-b border-[hsl(var(--border))] px-6 py-5">
          <div>
            <p className="font-serif text-[1.06rem] text-[hsl(var(--foreground))]">How I know</p>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{obligation.title}</p>
          </div>
          <p className="ml-auto pt-1 text-xs text-[hsl(var(--muted-foreground))]">
            {orderedEvents.length} event{orderedEvents.length === 1 ? "" : "s"}
          </p>
          <button
            ref={closeRef}
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--secondary))]"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-6 py-2">
          {orderedEvents.map((event) => {
            const Icon = iconFor(event.icon || event.type);
            return (
              <div
                key={event.id}
                className="flex gap-4 border-b border-[hsl(var(--border))/0.7] py-4 last:border-b-0"
                data-testid={`proof-event-${event.id}`}
              >
                <div className="w-7 shrink-0 pt-0.5 text-[hsl(var(--accent))]">
                  <Icon size={16} />
                </div>
                <div className="w-24 shrink-0 text-[11.5px] leading-5 text-[hsl(var(--muted-foreground))]">
                  {formatTimestamp(event.timestamp)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-[hsl(var(--foreground))]">{event.type}</p>
                    {event.seq !== null && event.seq !== undefined ? (
                      <span className="rounded-full bg-[hsl(var(--secondary))] px-2 py-0.5 text-[10.5px] uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                        #{event.seq}
                      </span>
                    ) : null}
                  </div>
                  {event.snippet ? (
                    <div className="mt-2 rounded-r-md border-l-2 border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-3 py-2 text-sm italic text-[hsl(var(--muted-foreground))]">
                      {event.snippet}
                    </div>
                  ) : null}
                  {onOpenEvidence ? (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--foreground))] underline decoration-[hsl(var(--accent))] underline-offset-4"
                        onClick={() => {
                          onOpenEvidence(obligation, event);
                        }}
                      >
                        Open source
                        <ArrowSquareOut size={13} />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 border-t border-[hsl(var(--border))] px-6 py-4 text-xs text-[hsl(var(--muted-foreground))]">
          <span>Every entry stays grounded in the original evidence.</span>
          <button
            type="button"
            className="ml-auto font-medium text-[hsl(var(--foreground))] underline decoration-[hsl(var(--accent))] underline-offset-4"
            onClick={() => onOpenEvidence && onOpenEvidence(obligation)}
          >
            Full evidence
          </button>
        </div>
      </div>
    </div>
  );
}
